import React, {
  useState, useCallback, useEffect, useContext, createContext,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { profileAPI } from '../../services/api';
import { toast } from 'sonner';
import { Pencil, Check, X, Copy } from 'lucide-react';

// ─── Context ──────────────────────────────────────────────────────────────────
const ProfileCtx = createContext(null);

// ─── Brand ────────────────────────────────────────────────────────────────────
const G    = '#1a5d3a';
const GOLD = '#f5a623';
const LG   = '#e8f2ec';
const CR   = '#f5f0e8'; // cream — field backgrounds only

// ─── Intake fields ────────────────────────────────────────────────────────────
const INTAKE_FIELDS = new Set([
  'first_name', 'last_name', 'email', 'birthdate', 'gender',
  'high_school_name', 'student_type', 'high_school_grad_year',
  'gpa', 'sat_score', 'act_score', 'intended_major', 'alternate_major',
  'rotc', 'preferred_learning_mode', 'college_size',
  'phone', 'address', 'city', 'state', 'postal_code', 'first_gen', 'ethnicity',
]);

// ─── Section field definitions ────────────────────────────────────────────────
const SECTION_FIELDS = {
  personal:    ['first_name', 'last_name', 'phone', 'parent_guardian_email', 'parent_guardian_name', 'parent_guardian_last_name', 'parent_guardian_phone'],
  academic:    ['high_school_name', 'gpa', 'counselor_name', 'high_school_grad_year', 'sat_score', 'act_score', 'intended_major', 'alternate_major', 'intended_enrollment_year'],
  location:    ['city', 'state', 'postal_code', 'address'],
  preferences: ['college_size', 'preferred_learning_mode', 'career_goal', 'first_gen', 'bilingual', 'rotc', 'plays_sports', 'currently_employed', 'military_affiliated', 'interested_volunteering', 'seeking_employment', 'income_bracket', 'interests'],
  about:       ['birthdate', 'gender', 'ethnicity', 'religion', 'self_description', 'daily_online_hours', 'instagram', 'tiktok', 'snapchat'],
};

const COMPLETION_SECTIONS = [
  { label: 'Personal Info', fields: SECTION_FIELDS.personal },
  { label: 'Academic',      fields: SECTION_FIELDS.academic },
  { label: 'Location',      fields: SECTION_FIELDS.location },
  { label: 'Preferences',   fields: ['college_size', 'preferred_learning_mode', 'career_goal'] },
  { label: 'Community',     fields: ['first_gen', 'bilingual', 'rotc', 'plays_sports', 'currently_employed', 'military_affiliated', 'income_bracket', 'interests'] },
  { label: 'About Me',      fields: ['birthdate', 'gender', 'ethnicity', 'religion', 'self_description', 'daily_online_hours', 'instagram', 'tiktok', 'snapchat'] },
];

const SCORE_FIELDS = [
  'first_name', 'last_name', 'profile_picture_url', 'birthdate',
  'high_school_name', 'high_school_grad_year', 'intended_major',
  'sat_score', 'act_score', 'gpa', 'phone', 'gender', 'ethnicity',
  'city', 'state', 'postal_code', 'first_gen', 'student_type', 'interests',
  'parent_guardian_email', 'counselor_name', 'career_goal',
  'daily_online_hours', 'income_bracket',
];

const WAVE_TIERS = [
  { at: 20,  reward: '$5 Amazon Gift Card' },
  { at: 50,  reward: 'Big Mac Meal' },
  { at: 100, reward: '$10 Amazon Gift Card' },
];

// ─── Helpers (data logic — untouched) ────────────────────────────────────────
const isFilled = (v) => {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'boolean') return true;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

const calcSectionPct = (user, fields) => {
  if (!user || !fields.length) return 0;
  return Math.round(fields.filter((f) => isFilled(user[f])).length / fields.length * 100);
};

const calcOverall = (user) => {
  const scores = COMPLETION_SECTIONS.map((s) => calcSectionPct(user, s.fields));
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

const calcSignalScore = (user) => {
  if (!user) return 0;
  const filled = SCORE_FIELDS.filter((f) => isFilled(user[f])).length;
  const social = (isFilled(user.instagram) || isFilled(user.tiktok) || isFilled(user.snapchat)) ? 1 : 0;
  return Math.round((filled + social) / (SCORE_FIELDS.length + 1) * 100);
};

// barColor used in Profile Completion card
const barColor = (pct) => {
  if (pct === 100) return G;
  if (pct >= 50)   return GOLD;
  return '#e53935';
};

const getNextTier = (waves) => {
  for (const t of WAVE_TIERS) {
    if ((waves || 0) < t.at) return t;
  }
  return null;
};

// ─── Date display helper: YYYY-MM-DD → MM/DD/YYYY ────────────────────────────
const fmtDate = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : v;
};

// ─── Option arrays ────────────────────────────────────────────────────────────
const GRAD_YEAR_OPTS    = ['2025', '2026', '2027', '2028', '2029'];
const ENROLL_YEAR_OPTS  = ['2025', '2026', '2027', '2028'];
const GENDER_OPTS       = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
const ETHNICITY_OPTS    = ['African American', 'Hispanic/Latino', 'Asian/Pacific Islander', 'White/Caucasian', 'Native American', 'Two or more races', 'Prefer not to say'];
const RELIGION_OPTS     = ['Christian', 'Catholic', 'Muslim', 'Jewish', 'Hindu', 'Buddhist', 'None', 'Prefer not to say'];
const COLLEGE_SIZE_OPTS = ['Small under 5K', 'Medium 5K-15K', 'Large over 15K', 'No preference'];
const LEARNING_OPTS     = ['In-person', 'Online', 'Hybrid/HyFlex', 'No preference'];
const HOURS_OPTS        = ['0-1 hours/day', '1-3 hours/day', '3-5 hours/day', '4-6 hours/day', '5+ hours/day'];
const INCOME_OPTS       = ['Under $30,000', '$30,000-$60,000', '$50,000-$75,000', '$60,000-$100,000', 'Over $100,000', 'Prefer not to say'];
const INTEREST_OPTIONS  = ['STEM', 'Arts', 'Business', 'Healthcare', 'Education', 'Law', 'Sports', 'Social Sciences', 'Technology', 'Entrepreneurship', 'Humanities', 'Environment'];

// ─── ScoreRing — 10% larger than previous (75×75, r=29, font 16) ──────────────
const ScoreRing = ({ score }) => {
  const r    = 29;
  const cx   = 37.5;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid="signal-score-ring">
      <svg width="75" height="75" viewBox="0 0 75 75">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="5" />
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke={GOLD} strokeWidth="5"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cx})`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        <text x={cx} y="44" textAnchor="middle" fontSize="16" fontWeight="700" fill="white" fontFamily="DM Sans, sans-serif">
          {score}%
        </text>
      </svg>
      <span style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        Signal Score
      </span>
    </div>
  );
};

// ─── FieldBox — 64px height, cream bg, green labels ──────────────────────────
const FieldBox = ({ label, fieldKey, value, type = 'text', opts = null, readOnly = false, placeholder = '', onSave }) => {
  const user = useContext(ProfileCtx);
  const [editing, setEditing] = useState(false);
  const [local, setLocal]     = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);

  const isIntake = INTAKE_FIELDS.has(fieldKey) && isFilled(value) && user?.onboarding_completed;

  const getBg     = () => readOnly ? '#ede8e0' : isIntake ? '#e8d4a8' : '#ffffff';
  const getBorder = () => readOnly ? '#d6d0c8' : isIntake ? '#c9ad75' : '#e2dbd2';

  const commit = async () => {
    try { await onSave(fieldKey, local); } finally { setEditing(false); }
  };
  const cancel = () => { setLocal(value ?? ''); setEditing(false); };

  if (editing) {
    return (
      <div
        className="rounded-xl px-3 py-2"
        style={{ border: `1.5px solid ${G}`, backgroundColor: '#fff', minHeight: '64px' }}
        data-testid={`profile-field-${fieldKey}`}
      >
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: G, marginBottom: '4px' }}>
          {label}
        </div>
        <div className="flex items-center gap-1.5">
          {opts ? (
            <select
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              autoFocus
              className="flex-1 outline-none bg-transparent"
              style={{ fontSize: '13px', color: local ? '#1a1a1a' : '#aaa', fontStyle: local ? 'normal' : 'italic' }}
              data-testid={`profile-input-${fieldKey}`}
            >
              <option value="">Pending</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              autoFocus
              rows={2}
              className="flex-1 outline-none bg-transparent resize-none"
              style={{ fontSize: '13px', color: '#1a1a1a' }}
              data-testid={`profile-input-${fieldKey}`}
            />
          ) : (
            <input
              type={type}
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              autoFocus
              placeholder={placeholder || undefined}
              className="flex-1 outline-none bg-transparent min-w-0"
              style={{ fontSize: '13px', color: '#1a1a1a' }}
              data-testid={`profile-input-${fieldKey}`}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
            />
          )}
          <button onClick={commit} className="flex-shrink-0 p-1.5 rounded-lg" style={{ backgroundColor: G, color: '#fff' }} data-testid={`profile-save-${fieldKey}`}>
            <Check size={12} />
          </button>
          <button onClick={cancel} className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100" data-testid={`profile-cancel-${fieldKey}`}>
            <X size={12} style={{ color: '#888' }} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group relative rounded-xl px-3 flex flex-col justify-center"
      style={{ height: '64px', backgroundColor: getBg(), border: `1.5px solid ${getBorder()}` }}
      data-testid={`profile-field-${fieldKey}`}
    >
      {/* SIGNUP badge — top right */}
      {isIntake && (
        <span
          className="absolute top-1.5 right-2"
          style={{ fontSize: '8px', fontWeight: 700, backgroundColor: GOLD, color: '#fff', padding: '1px 5px', borderRadius: '4px' }}
          data-testid={`intake-badge-${fieldKey}`}
        >
          SIGNUP
        </span>
      )}
      {/* Label — dark green */}
      <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: G, marginBottom: '4px' }}>
        {label}
      </div>
      {isFilled(value) ? (
        <span className="truncate pr-4" style={{ fontSize: '15px', fontWeight: 500, color: '#1a1a1a' }}>
          {fieldKey === 'birthdate' ? fmtDate(value) : String(value)}
        </span>
      ) : (
        <span style={{ fontSize: '13px', fontStyle: 'italic', color: '#b8b0a8' }}>
          {placeholder || 'Pending'}
        </span>
      )}
      {!readOnly && (
        <button
          onClick={() => setEditing(true)}
          className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg"
          style={{ color: G }}
          data-testid={`profile-edit-${fieldKey}`}
          aria-label={`Edit ${label}`}
        >
          <Pencil size={12} />
        </button>
      )}
    </div>
  );
};

// ─── MiniToggle ───────────────────────────────────────────────────────────────
const MiniToggle = ({ label, fieldKey, value, onSave }) => {
  const [checked, setChecked] = useState(!!value);
  useEffect(() => { setChecked(!!value); }, [value]);

  const toggle = async () => {
    const next = !checked;
    setChecked(next);
    await onSave(fieldKey, next);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className="flex items-center gap-1.5 px-2.5 rounded-xl w-full text-left"
      style={{ height: '34px', backgroundColor: '#fff', border: '1.5px solid #e8e0d8' }}
      data-testid={`profile-toggle-${fieldKey}`}
    >
      <div
        className="relative flex-shrink-0 rounded-full transition-colors duration-200"
        style={{ width: '24px', height: '13px', backgroundColor: checked ? G : '#ccc' }}
      >
        <div
          className="absolute top-[1px] rounded-full bg-white transition-transform duration-200"
          style={{ width: '11px', height: '11px', transform: checked ? 'translateX(12px)' : 'translateX(1px)' }}
        />
      </div>
      <span className="flex-1 truncate" style={{ fontSize: '8px', fontWeight: 600, color: '#4a5e52' }}>
        {label}
      </span>
    </button>
  );
};

// ─── TagsEditor ───────────────────────────────────────────────────────────────
const TagsEditor = ({ value, fieldKey, onSave, opts = INTEREST_OPTIONS }) => {
  const arr = Array.isArray(value) ? value : [];
  const [editing, setEditing] = useState(false);
  const [local, setLocal]     = useState(arr);
  useEffect(() => { setLocal(Array.isArray(value) ? value : []); }, [value]);

  const toggle = (tag) => setLocal((p) => p.includes(tag) ? p.filter((t) => t !== tag) : [...p, tag]);
  const commit = async () => { await onSave(fieldKey, local); setEditing(false); };

  return (
    <div data-testid={`profile-field-${fieldKey}`}>
      <div className="flex flex-wrap gap-1.5 items-center">
        {local.length > 0
          ? local.map((tag) => (
            /* Interest tags: #e8f2ec bg, dark green text */
            <span key={tag} className="px-2.5 py-0.5 rounded-full" style={{ backgroundColor: LG, color: G, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              {tag}
            </span>
          ))
          : <span style={{ fontSize: '13px', fontStyle: 'italic', color: '#b8b0a8' }}>Pending</span>
        }
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-2 py-0.5 rounded-full"
            style={{ fontSize: '11px', backgroundColor: '#eee', color: '#888' }}
            data-testid={`profile-edit-${fieldKey}`}
          >
            + Add more
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {opts.map((tag) => (
              <button
                key={tag}
                onClick={() => toggle(tag)}
                className="px-2 py-0.5 rounded-full text-xs font-medium transition-colors"
                style={local.includes(tag) ? { backgroundColor: G, color: '#fff' } : { backgroundColor: CR, color: G }}
                data-testid={`tag-opt-${tag.toLowerCase().replace(/\s/g, '-')}`}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={commit} className="px-3 py-1 rounded text-xs text-white font-semibold" style={{ backgroundColor: G }} data-testid={`profile-save-${fieldKey}`}>Save</button>
            <button onClick={() => { setLocal(arr); setEditing(false); }} className="px-3 py-1 rounded text-xs hover:bg-gray-100" style={{ color: '#6B7280' }} data-testid={`profile-cancel-${fieldKey}`}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SubSectionLabel ──────────────────────────────────────────────────────────
const SubSectionLabel = ({ children }) => (
  <div className="mt-4 mb-2">
    <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8fa898' }}>
      {children}
    </span>
  </div>
);

// ─── SectionCard — solid green header bar, white content area ─────────────────
const SectionCard = ({ title, sectionKey, user, children }) => {
  const fields = SECTION_FIELDS[sectionKey] || [];
  const pct    = calcSectionPct(user, fields);
  const [open, setOpen] = useState(pct < 100);

  return (
    <div
      className="overflow-hidden"
      style={{ backgroundColor: CR, borderRadius: '8px', marginBottom: '1.25rem' }}
      data-testid={`section-${sectionKey}`}
    >
      {/* Solid green header — white title, gold %, toggle on right */}
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-3 px-5 py-3.5"
        style={{ backgroundColor: G }}
        data-testid={`section-toggle-${sectionKey}`}
      >
        <span className="flex-1 text-left font-semibold" style={{ fontSize: '14px', fontFamily: 'Playfair Display, serif', color: '#fff' }}>
          {title}
        </span>
        <span className="flex-shrink-0 font-bold" style={{ fontSize: '13px', color: GOLD }}>
          {pct}%
        </span>
        <span className="flex-shrink-0 font-bold select-none ml-1" style={{ fontSize: '16px', color: '#fff', lineHeight: 1 }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {/* White content area — max-height animation */}
      <div
        style={{
          maxHeight: open ? '3000px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.35s ease',
          backgroundColor: CR,
        }}
      >
        <div style={{ padding: '1.25rem' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ─── Self Description inline edit ────────────────────────────────────────────
const SelfDescriptionField = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal]     = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);

  const commit = async () => {
    try { await onSave('self_description', local); } finally { setEditing(false); }
  };
  const cancel = () => { setLocal(value ?? ''); setEditing(false); };

  return (
    <>
      <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: G, marginBottom: '6px' }}>
        Self Description
      </div>
      {editing ? (
        <div>
          <textarea
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            autoFocus
            rows={3}
            placeholder="Tell colleges who you are..."
            className="w-full outline-none bg-transparent resize-none"
            style={{ fontSize: '13px', color: '#1a1a1a' }}
            data-testid="profile-input-self_description"
          />
          <div className="flex gap-2 mt-2">
            <button onClick={commit} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-white font-semibold" style={{ backgroundColor: G }} data-testid="profile-save-self_description">
              <Check size={11} /> Save
            </button>
            <button onClick={cancel} className="flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-gray-100" style={{ fontSize: '12px', color: '#6B7280' }} data-testid="profile-cancel-self_description">
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="group flex items-start justify-between gap-2">
          {isFilled(value)
            ? <p style={{ fontSize: '15px', fontWeight: 500, color: '#1a1a1a', lineHeight: 1.5 }}>{value}</p>
            : <p style={{ fontSize: '13px', fontStyle: 'italic', color: '#b8b0a8' }}>Tell colleges who you are...</p>
          }
          <button
            onClick={() => setEditing(true)}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg"
            style={{ color: G }}
            data-testid="profile-edit-self_description"
            aria-label="Edit Self Description"
          >
            <Pencil size={12} />
          </button>
        </div>
      )}
    </>
  );
};

// ─── LEFT COLUMN CARDS ────────────────────────────────────────────────────────

const AvatarCard = ({ user }) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Student';
  const initials = (() => {
    if (user?.first_name && user?.last_name) return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    if (user?.first_name) return user.first_name.substring(0, 2).toUpperCase();
    if (user?.email)      return user.email.substring(0, 2).toUpperCase();
    return 'SS';
  })();

  return (
    <div className="p-4 flex flex-col items-center text-center" style={{ backgroundColor: G, border: '1.5px solid #144a2e', borderRadius: '8px' }} data-testid="profile-avatar-card">
      <div
        className="flex items-center justify-center overflow-hidden mb-3 flex-shrink-0"
        style={{ width: '110px', height: '110px', backgroundColor: GOLD, borderRadius: '12px', outline: '3px solid rgba(255,255,255,0.25)', outlineOffset: '3px' }}
        data-testid="profile-avatar"
      >
        {user?.profile_picture_url
          ? <img src={user.profile_picture_url} alt={fullName} className="w-full h-full object-cover" />
          : <span className="text-3xl font-bold" style={{ color: '#1a1a1a' }}>{initials}</span>
        }
      </div>
      <h2 className="font-bold mb-0.5" style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#ffffff' }}>
        {fullName}
      </h2>
      <p className="text-xs mb-3 truncate w-full" style={{ color: 'rgba(255,255,255,0.7)' }}>{user?.email || ''}</p>
      <button
        type="button"
        className="w-full py-2 font-semibold transition-opacity hover:opacity-90"
        style={{ backgroundColor: GOLD, color: '#1a1a1a', fontSize: '12px', borderRadius: '8px' }}
        data-testid="add-profile-photo-btn"
      >
        Add Profile Photo
      </button>
    </div>
  );
};

const MeetMeCard = ({ user }) => (
  <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: G }} data-testid="meet-me-card">
    <div className="px-4 pt-4 pb-1 relative">
      <span
        className="absolute top-3 right-3 px-2 py-0.5 rounded font-bold"
        style={{ fontSize: '8px', backgroundColor: GOLD, color: '#1a1a1a' }}
      >
        MOST IMPACTFUL
      </span>
      <h3 className="font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif', fontSize: '14px' }}>
        Meet Me
      </h3>
      <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
        A short video for college admissions to get to know you better
      </p>
    </div>
    <div className="px-4 pb-4 pt-2">
      <a
        href="/amg/record"
        className="flex items-center justify-center w-full py-2.5 font-semibold transition-opacity hover:opacity-90"
        style={{ backgroundColor: GOLD, color: '#1a1a1a', fontSize: '12px', borderRadius: '8px' }}
        data-testid="record-meet-me-btn"
      >
        {user?.amg_completed ? 'Re-record My Meet Me' : '\u25B6 Record My Meet Me'}
      </a>
      <p className="mt-2 text-center" style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>
        +10 Signal Waves when complete
      </p>
    </div>
  </div>
);

const QuadConnectCard = ({ user, onSave }) => {
  const [checked, setChecked] = useState(!!user?.available_for_qc411);
  useEffect(() => { setChecked(!!user?.available_for_qc411); }, [user?.available_for_qc411]);

  const toggle = async () => {
    const next = !checked;
    setChecked(next);
    await onSave('available_for_qc411', next);
  };

  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: '#fff', border: '1.5px solid #e8e0d8' }} data-testid="quadconnect-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold mb-0.5" style={{ fontSize: '13px', color: G }}>QuadConnect</h3>
          <p style={{ fontSize: '10px', color: '#6B7280', lineHeight: 1.4 }}>
            Live connect with an admissions or enrollment representative
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="flex-shrink-0 mt-0.5"
          style={{ width: '32px', height: '18px', borderRadius: '9px', backgroundColor: checked ? G : '#ccc', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }}
          data-testid="quadconnect-toggle"
          aria-label="Toggle QuadConnect"
        >
          <div
            style={{ position: 'absolute', top: '2px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#fff', transition: 'transform 0.2s', transform: checked ? 'translateX(16px)' : 'translateX(2px)' }}
          />
        </button>
      </div>
    </div>
  );
};

/* Speak with Oz — solid green card, white text, Oz image */
const OZ_IMG = '/oz.png';

const SpeakWithOzCard = () => (
  <a
    href="/advisor"
    className="flex items-center gap-3 rounded-2xl p-4 transition-opacity hover:opacity-90"
    style={{ backgroundColor: G, textDecoration: 'none' }}
    data-testid="speak-with-oz-btn"
  >
    <img
      src={OZ_IMG}
      alt="Ask Oz"
      style={{ width: '56px', height: '56px', borderRadius: '50%', border: `2.5px solid ${GOLD}`, objectFit: 'cover', flexShrink: 0 }}
    />
    <div className="min-w-0">
      <p className="font-bold" style={{ fontSize: '13px', color: '#fff', fontFamily: 'Playfair Display, serif' }}>Speak with Oz</p>
      <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>Your personal AI college advisor</p>
    </div>
  </a>
);

const SignalWavesCard = ({ user, onSave: _onSave }) => {
  const [copied, setCopied] = useState(false);
  const waves    = user?.signal_waves || 0;
  const code     = user?.wave_referral_code || '';
  const nextTier = getNextTier(waves);
  const prevAt   = nextTier
    ? (WAVE_TIERS.findIndex((t) => t.at === nextTier.at) > 0
       ? WAVE_TIERS[WAVE_TIERS.findIndex((t) => t.at === nextTier.at) - 1].at
       : 0)
    : WAVE_TIERS[WAVE_TIERS.length - 1].at;
  const pctNext = nextTier
    ? Math.min(100, Math.round(((waves - prevAt) / (nextTier.at - prevAt)) * 100))
    : 100;

  const copyLink = () => {
    if (!code) return;
    navigator.clipboard.writeText(`https://studentsignal.com?wave=${code}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast.error('Could not copy link'));
  };

  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: '#fff', border: '1.5px solid #e8e0d8' }} data-testid="signal-waves-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span style={{ fontSize: '15px' }}>🌊</span>
          <h3 className="font-semibold" style={{ fontSize: '13px', fontFamily: 'Playfair Display, serif', color: G }}>
            Signal Waves
          </h3>
        </div>
        <span className="font-bold" style={{ fontSize: '20px', color: GOLD }}>{waves}</span>
      </div>
      {nextTier && (
        <>
          <p style={{ fontSize: '10px', color: '#6B7280', marginBottom: '5px' }}>
            {nextTier.at - waves} more &rarr; <strong style={{ color: GOLD }}>{nextTier.reward}</strong>
          </p>
          <div className="rounded-full mb-3" style={{ height: '5px', backgroundColor: '#e8e0d8' }}>
            <div className="rounded-full transition-all duration-500" style={{ height: '5px', width: `${pctNext}%`, backgroundColor: GOLD }} />
          </div>
        </>
      )}
      {code ? (
        <button
          type="button"
          onClick={copyLink}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-full font-semibold transition-opacity hover:opacity-90"
          style={{ backgroundColor: LG, color: G, fontSize: '11px' }}
          data-testid="copy-wave-link"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied!' : 'Copy My Referral Link'}
        </button>
      ) : (
        <p style={{ fontSize: '10px', fontStyle: 'italic', color: '#b8b0a8' }}>Generating referral link…</p>
      )}
    </div>
  );
};

const UpcomingEventsCard = () => (
  <div className="rounded-2xl p-4" style={{ backgroundColor: '#fff', border: '1.5px solid #e8e0d8' }} data-testid="upcoming-events-card">
    <h3 className="font-semibold mb-2" style={{ fontSize: '13px', fontFamily: 'Playfair Display, serif', color: G }}>
      Upcoming Signal Events
    </h3>
    <p style={{ fontSize: '10px', fontStyle: 'italic', color: '#b8b0a8' }}>
      No upcoming events — check back soon
    </p>
  </div>
);

const ProfileCompletionCard = ({ user, onScrollToFirst }) => {
  const overall = calcOverall(user);

  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: '#fff', border: '1.5px solid #e8e0d8' }} data-testid="profile-progress-card">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="font-semibold" style={{ fontSize: '13px', fontFamily: 'Playfair Display, serif', color: G }}>
          Profile Completion
        </h3>
        <span className="font-bold" style={{ fontSize: '13px', color: barColor(overall) }}>{overall}%</span>
      </div>
      <div className="rounded-full mb-3" style={{ height: '5px', backgroundColor: '#e8e0d8' }}>
        <div className="rounded-full transition-all duration-500" style={{ height: '5px', width: `${overall}%`, backgroundColor: barColor(overall) }} />
      </div>
      <div className="space-y-2 mb-3">
        {COMPLETION_SECTIONS.map((s) => {
          const pct = calcSectionPct(user, s.fields);
          return (
            <div key={s.label}>
              <div className="flex items-center justify-between mb-0.5">
                <span style={{ fontSize: '10px', color: '#6B7280' }}>{s.label}</span>
                <span className="font-bold" style={{ fontSize: '10px', color: barColor(pct) }}>{pct}%</span>
              </div>
              <div className="rounded-full" style={{ height: '3px', backgroundColor: '#e8e0d8' }}>
                <div className="rounded-full transition-all duration-500" style={{ height: '3px', width: `${pct}%`, backgroundColor: barColor(pct) }} />
              </div>
            </div>
          );
        })}
      </div>
      {overall < 100 && (
        <button
          type="button"
          onClick={onScrollToFirst}
          className="w-full py-2.5 rounded-xl font-semibold transition-opacity hover:opacity-90"
          style={{ backgroundColor: G, color: '#fff', fontSize: '11px' }}
          data-testid="complete-profile-btn"
        >
          Complete My Profile &rarr;
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SignalProfile = () => {
  const { user, updateUser } = useAuth();

  useEffect(() => {
    if (user && !user.wave_referral_code && user.first_name) {
      const fn   = (user.first_name || '').toUpperCase().replace(/[^A-Z]/g, '').substring(0, 4);
      const li   = ((user.last_name || '')[0] || '').toUpperCase();
      const yr   = user.high_school_grad_year || String(new Date().getFullYear() + 1);
      const code = `${fn}${li}${yr}`;
      profileAPI.updateProfile({ wave_referral_code: code })
        .then(() => updateUser({ wave_referral_code: code }))
        .catch(() => {});
    }
  }, [user, updateUser]);

  const handleSave = useCallback(async (field, value) => {
    try {
      await profileAPI.updateProfile({ [field]: value });
      updateUser({ [field]: value });
      toast.success('Saved');
    } catch {
      toast.error('Failed to save — please try again');
    }
  }, [updateUser]);

  const signalScore = calcSignalScore(user);

  const scrollToFirst = useCallback(() => {
    const order = ['personal', 'academic', 'location', 'preferences', 'about'];
    for (const id of order) {
      if (calcSectionPct(user, SECTION_FIELDS[id]) < 100) {
        document.querySelector(`[data-testid="section-${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
  }, [user]);

  return (
    <ProfileCtx.Provider value={user}>
      {/* Page background: #faf9f6 */}
      <div style={{ backgroundColor: '#faf9f6', minHeight: '100vh', fontFamily: 'DM Sans, sans-serif' }} data-testid="signal-profile-page">

        {/* ── HEADER BAR ─────────────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-8 py-5"
          style={{ backgroundColor: G }}
          data-testid="profile-header-bar"
        >
          <div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
              Signal Profile
            </h1>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
              Your profile matches you with colleges, scholarships and opportunities
            </p>
          </div>
          {/* ScoreRing: 10% larger — 75×75, r=29, font 16 */}
          <ScoreRing score={signalScore} />
        </div>

        {/* ── 3-COLUMN LAYOUT ─────────────────────────────────────────────── */}
        <div className="flex items-start p-4" style={{ gap: '8px' }} data-testid="profile-layout">

          {/* ── LEFT COLUMN (280px) ─────────────────────────────────────── */}
          <div className="flex-shrink-0 flex flex-col gap-3" style={{ width: '280px' }} data-testid="profile-left-column">
            <AvatarCard user={user} />
            <MeetMeCard user={user} />
            <SpeakWithOzCard />
            <QuadConnectCard user={user} onSave={handleSave} />
            <SignalWavesCard user={user} onSave={handleSave} />
            <UpcomingEventsCard />
            <ProfileCompletionCard user={user} onScrollToFirst={scrollToFirst} />
          </div>

          {/* ── MIDDLE COLUMN — Personal Info, Location, About Me ────────── */}
          <div className="flex-1 min-w-0" data-testid="profile-middle-column">

            {/* Section 1 — Personal Info */}
            <SectionCard title="Personal Info" sectionKey="personal" user={user}>
              <div className="grid grid-cols-3 gap-2.5 mb-2.5">
                <FieldBox label="First Name" fieldKey="first_name" value={user?.first_name} onSave={handleSave} />
                <FieldBox label="Last Name"  fieldKey="last_name"  value={user?.last_name}  onSave={handleSave} />
                <FieldBox label="Phone"      fieldKey="phone"      value={user?.phone}       type="tel" onSave={handleSave} />
              </div>
              <div className="grid grid-cols-2 gap-2.5 mb-2.5">
                <FieldBox label="Email"                 fieldKey="email"                 value={user?.email}                readOnly onSave={handleSave} />
                <FieldBox label="Parent/Guardian Email" fieldKey="parent_guardian_email" value={user?.parent_guardian_email} type="email" onSave={handleSave} />
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                <FieldBox label="Parent First Name"     fieldKey="parent_guardian_name"      value={user?.parent_guardian_name}      onSave={handleSave} />
                <FieldBox label="Parent Last Name"      fieldKey="parent_guardian_last_name" value={user?.parent_guardian_last_name} onSave={handleSave} />
                <FieldBox label="Parent/Guardian Phone" fieldKey="parent_guardian_phone"     value={user?.parent_guardian_phone}     type="tel" onSave={handleSave} />
              </div>
            </SectionCard>

            {/* Section 3 — Location */}
            <SectionCard title="Location" sectionKey="location" user={user}>
              <div className="mb-2.5">
                <FieldBox label="Address" fieldKey="address" value={user?.address} onSave={handleSave} />
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                <FieldBox label="City"        fieldKey="city"        value={user?.city}        onSave={handleSave} />
                <FieldBox label="State"       fieldKey="state"       value={user?.state}       onSave={handleSave} />
                <FieldBox label="Postal Code" fieldKey="postal_code" value={user?.postal_code} onSave={handleSave} />
              </div>
            </SectionCard>

            {/* Section 5 — About Me */}
            <SectionCard title="About Me" sectionKey="about" user={user}>
              {/* Date of Birth + Gender: 2-col; Ethnicity + Religion: 2-col */}
              <div className="grid grid-cols-2 gap-2.5 mb-2.5">
                {/* Date of Birth: type=text, placeholder MM/DD/YYYY */}
                <FieldBox label="Date of Birth" fieldKey="birthdate"  value={user?.birthdate}  type="text" placeholder="MM/DD/YYYY" onSave={handleSave} />
                <FieldBox label="Gender"        fieldKey="gender"     value={user?.gender}     opts={GENDER_OPTS}    onSave={handleSave} />
              </div>
              <div className="grid grid-cols-2 gap-2.5 mb-2.5">
                <FieldBox label="Ethnicity" fieldKey="ethnicity" value={user?.ethnicity} opts={ETHNICITY_OPTS} onSave={handleSave} />
                <FieldBox label="Religion"  fieldKey="religion"  value={user?.religion}  opts={RELIGION_OPTS}  onSave={handleSave} />
              </div>
              <div className="grid grid-cols-2 gap-2.5 mb-2.5">
                <FieldBox label="Daily Hours Online" fieldKey="daily_online_hours" value={user?.daily_online_hours} opts={HOURS_OPTS} onSave={handleSave} />
                <div />
              </div>
              <SubSectionLabel>Social Media</SubSectionLabel>
              <div className="grid grid-cols-3 gap-2.5 mb-2.5">
                <FieldBox label="Instagram" fieldKey="instagram" value={user?.instagram} onSave={handleSave} />
                <FieldBox label="TikTok"    fieldKey="tiktok"    value={user?.tiktok}    onSave={handleSave} />
                <FieldBox label="Snapchat"  fieldKey="snapchat"  value={user?.snapchat}  onSave={handleSave} />
              </div>
              <div
                className="group relative rounded-xl px-4 py-3"
                style={{ backgroundColor: '#ffffff', border: '1.5px solid #e2dbd2', minHeight: '64px' }}
                data-testid="profile-field-self_description"
              >
                <SelfDescriptionField value={user?.self_description} onSave={handleSave} />
              </div>
            </SectionCard>

          </div>

          {/* ── RIGHT COLUMN — Academic Profile, Preferences & Community ── */}
          <div className="flex-1 min-w-0" data-testid="profile-right-column">

            {/* Section 2 — Academic Profile
                Row 1: High School Name — full width, own row
                Row 2: GPA (40%) | SAT Score | ACT Score
                Row 3: Graduation Year | Enrollment Year | Counselor Name (slightly wider)
                Row 4: Intended Major | Alternate Major                                    */}
            <SectionCard title="Academic Profile" sectionKey="academic" user={user}>
              {/* Row 1: High School Name — full width */}
              <div className="mb-2.5">
                <FieldBox label="High School Name" fieldKey="high_school_name" value={user?.high_school_name} onSave={handleSave} />
              </div>
              {/* Row 2: GPA 40% | SAT Score | ACT Score */}
              <div className="grid gap-2.5 mb-2.5" style={{ gridTemplateColumns: '40% 1fr 1fr' }}>
                <FieldBox label="GPA"       fieldKey="gpa"       value={user?.gpa}       onSave={handleSave} />
                <FieldBox label="SAT Score" fieldKey="sat_score" value={user?.sat_score} onSave={handleSave} />
                <FieldBox label="ACT Score" fieldKey="act_score" value={user?.act_score} onSave={handleSave} />
              </div>
              {/* Row 3: Grad Year | Enrollment Year | Counselor Name (1.3× wider) */}
              <div className="grid gap-2.5 mb-2.5" style={{ gridTemplateColumns: '1fr 1fr 1.3fr' }}>
                <FieldBox label="Graduation Year"  fieldKey="high_school_grad_year"  value={user?.high_school_grad_year}  opts={GRAD_YEAR_OPTS}   onSave={handleSave} />
                <FieldBox label="Enrollment Year"  fieldKey="intended_enrollment_year" value={user?.intended_enrollment_year} opts={ENROLL_YEAR_OPTS} onSave={handleSave} />
                <FieldBox label="Counselor's Name" fieldKey="counselor_name"           value={user?.counselor_name}           onSave={handleSave} />
              </div>
              {/* Row 4: Intended Major | Alternate Major */}
              <div className="grid grid-cols-2 gap-2.5">
                <FieldBox label="Intended Major"  fieldKey="intended_major"  value={user?.intended_major}  onSave={handleSave} />
                <FieldBox label="Alternate Major" fieldKey="alternate_major" value={user?.alternate_major} onSave={handleSave} />
              </div>
            </SectionCard>

            {/* Section 4 — Preferences & Community */}
            <SectionCard title="Preferences &amp; Community" sectionKey="preferences" user={user}>

              <SubSectionLabel>College Preferences</SubSectionLabel>
              <div className="grid grid-cols-3 gap-2.5 mb-1">
                <FieldBox label="College Size"            fieldKey="college_size"           value={user?.college_size}           opts={COLLEGE_SIZE_OPTS} onSave={handleSave} />
                <FieldBox label="Preferred Learning Mode" fieldKey="preferred_learning_mode" value={user?.preferred_learning_mode} opts={LEARNING_OPTS}     onSave={handleSave} />
                <FieldBox label="Career Goal"             fieldKey="career_goal"            value={user?.career_goal}            onSave={handleSave} />
              </div>

              <SubSectionLabel>Community &amp; Involvement</SubSectionLabel>
              <div className="grid grid-cols-3 gap-2 mb-2.5">
                <MiniToggle label="First Gen Student"        fieldKey="first_gen"               value={user?.first_gen}               onSave={handleSave} />
                <MiniToggle label="Bilingual"                fieldKey="bilingual"               value={user?.bilingual}               onSave={handleSave} />
                <MiniToggle label="ROTC Interest"            fieldKey="rotc"                    value={user?.rotc}                    onSave={handleSave} />
                <MiniToggle label="HS Varsity Sports"        fieldKey="plays_sports"            value={user?.plays_sports}            onSave={handleSave} />
                <MiniToggle label="Currently Employed"       fieldKey="currently_employed"      value={user?.currently_employed}      onSave={handleSave} />
                <MiniToggle label="Military Affiliated"      fieldKey="military_affiliated"     value={user?.military_affiliated}     onSave={handleSave} />
                <MiniToggle label="Volunteering"             fieldKey="interested_volunteering" value={user?.interested_volunteering} onSave={handleSave} />
                <MiniToggle label="Seeking Employment"       fieldKey="seeking_employment"      value={user?.seeking_employment}      onSave={handleSave} />
                <MiniToggle label="Interested in Esports"    fieldKey="interested_esports"      value={user?.interested_esports}      onSave={handleSave} />
                <MiniToggle label="Streams YouTube/Twitch"   fieldKey="streams_youtube_twitch"  value={user?.streams_youtube_twitch}  onSave={handleSave} />
                <MiniToggle label="Interested in Mentorship" fieldKey="interested_mentorship"   value={user?.interested_mentorship}   onSave={handleSave} />
                <MiniToggle label="Need Tutoring"            fieldKey="need_tutoring"           value={user?.need_tutoring}           onSave={handleSave} />
              </div>
              <div className="grid grid-cols-2 gap-2.5 mb-1">
                <FieldBox label="Income Bracket" fieldKey="income_bracket" value={user?.income_bracket} opts={INCOME_OPTS} onSave={handleSave} />
                <div />
              </div>

              <SubSectionLabel>Interests</SubSectionLabel>
              <div className="mb-3">
                <TagsEditor value={user?.interests} fieldKey="interests" onSave={handleSave} opts={INTEREST_OPTIONS} />
              </div>

              {/* Earn Rewards card */}
              <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ backgroundColor: LG, border: '1.5px solid #c8e6d4' }}>
                <div>
                  <p className="font-bold" style={{ fontSize: '12px', color: G }}>Earn Rewards for Posting Online</p>
                  <p style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>
                    Share StudentSignal content and earn Signal Waves
                  </p>
                </div>
                <MiniToggle label="" fieldKey="earn_rewards_posting" value={user?.earn_rewards_posting} onSave={handleSave} />
              </div>

            </SectionCard>

          </div>
        </div>
      </div>
    </ProfileCtx.Provider>
  );
};

export default SignalProfile;
